import { Document, Page as PdfPage, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { registerPdfFonts } from './pdf-fonts';

registerPdfFonts();

// ─── Membership readiness packet PDF (Camino TMAI · R0-PR6) ──────────────────
// The aggregate, ZERO-PII artifact a TC carries to the TMAI board. Mirrors
// CapProposalPdfDocument: single StyleSheet, watermark, footer + paging, EB
// Garamond. i18n-AGNOSTIC — receives labels already resolved to ENGLISH (TMAI
// submissions are in English), regardless of the UI language. Rendered
// client-side (pdf().toBlob()) from a freshness-gated readiness snapshot.
//
// no_data criteria are NOT presented as non-compliance and NOT silently omitted:
// they get their own neutral "not yet assessable" bucket (director's ruling).

export interface MembershipPacketCriterion {
  label: string; // English criterion label
  category: string; // English category label
  /** Aggregate origin count for a derived indicator, e.g. "18 of 22". No PII. */
  origin?: string;
}

export interface MembershipPacketPdfData {
  brand: {
    tenantName: string;
    logoUrl?: string;
    primaryColor: string;
    tmaiSiteAbbreviation?: string;
  };
  stage: string; // English stage label
  generatedOn: string;
  overallStatus: 'ready' | 'with_gaps';
  byCategory: Array<{ label: string; met: number; total: number }>;
  met: MembershipPacketCriterion[];
  gaps: MembershipPacketCriterion[];
  notEvaluable: MembershipPacketCriterion[];
  informative: MembershipPacketCriterion[];
  labels: {
    title: string;
    watermarkDraft: string;
    stage: string;
    generatedOn: string;
    overallStatusTitle: string;
    overallReady: string;
    overallWithGaps: string;
    progressByCategory: string;
    metTitle: string;
    gapsTitle: string;
    notEvaluableTitle: string;
    notEvaluableReason: string;
    informativeTitle: string;
    boardDecides: string;
    footerDisclaimer: string;
    emptyBucket: string;
  };
}

const styles = StyleSheet.create({
  page: {
    fontFamily: 'EB Garamond',
    fontSize: 10,
    color: '#1f2937',
    paddingTop: 42,
    paddingBottom: 56,
    paddingHorizontal: 48,
  },
  watermark: {
    position: 'absolute',
    top: '45%',
    left: 0,
    right: 0,
    textAlign: 'center',
    // 36pt keeps "DRAFT — NOT OFFICIAL" on ONE line across A4 — at 54pt it
    // wrapped and hyphenated ("NOT OFFI-CIAL") along the diagonal.
    fontSize: 36,
    fontWeight: 700,
    letterSpacing: 2,
    color: '#000000',
    opacity: 0.06,
    transform: 'rotate(-45deg)',
  },
  header: { textAlign: 'center', marginBottom: 16 },
  logo: { height: 48, objectFit: 'contain', marginBottom: 6, alignSelf: 'center' },
  tenantName: { fontSize: 16, fontWeight: 700, lineHeight: 1.2 },
  brandBar: { width: 64, height: 3, borderRadius: 2, alignSelf: 'center', marginVertical: 7 },
  docTitle: { fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' },
  metaRow: { flexDirection: 'row', gap: 24, justifyContent: 'center', marginTop: 4 },
  metaLabel: { fontSize: 7, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  metaValue: { fontSize: 10, fontWeight: 700 },
  boardBox: {
    borderWidth: 1,
    borderColor: '#c7d2fe',
    borderRadius: 4,
    padding: 8,
    marginBottom: 14,
    backgroundColor: '#eef2ff',
    fontSize: 9,
    color: '#3730a3',
    textAlign: 'center',
  },
  overallRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  overallTitle: { fontSize: 11, fontWeight: 700 },
  statusChip: {
    fontSize: 8,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
  },
  chipReady: { backgroundColor: '#dcfce7', color: '#166534' },
  chipGaps: { backgroundColor: '#fef3c7', color: '#92400e' },
  sectionLabel: {
    fontSize: 8,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 700,
    color: '#6b7280',
    marginBottom: 4,
    marginTop: 6,
  },
  catRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  catName: { fontSize: 9.5, color: '#374151' },
  catCount: { fontSize: 9.5, color: '#6b7280', fontWeight: 700 },
  bucket: { marginBottom: 12 },
  bucketHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
    paddingBottom: 3,
  },
  bucketTitle: { fontSize: 11, fontWeight: 700 },
  bucketReason: { fontSize: 8.5, color: '#64748b', fontStyle: 'italic', marginBottom: 4 },
  critRow: { flexDirection: 'row', marginBottom: 3, gap: 6 },
  critDot: { fontSize: 9.5, width: 8 },
  critBody: { flex: 1 },
  critLabel: { fontSize: 9.5, lineHeight: 1.35, color: '#374151' },
  critMeta: { fontSize: 7.5, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 },
  emptyNote: { fontSize: 9, color: '#94a3b8', fontStyle: 'italic' },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    fontSize: 8,
    textAlign: 'center',
    color: '#94a3b8',
    borderTopWidth: 0.5,
    borderTopColor: '#e2e8f0',
    paddingTop: 6,
  },
  pageNumber: { position: 'absolute', bottom: 10, right: 48, fontSize: 8, color: '#94a3b8' },
});

function Bucket({
  title,
  reason,
  dotColor,
  items,
  emptyLabel,
}: {
  title: string;
  reason?: string;
  dotColor: string;
  items: MembershipPacketCriterion[];
  emptyLabel: string;
}): JSX.Element {
  return (
    <View style={styles.bucket}>
      <View style={styles.bucketHead}>
        <Text style={styles.bucketTitle}>{title}</Text>
        <Text style={styles.catCount}>({items.length})</Text>
      </View>
      {reason ? <Text style={styles.bucketReason}>{reason}</Text> : null}
      {items.length === 0 ? (
        <Text style={styles.emptyNote}>{emptyLabel}</Text>
      ) : (
        items.map((c, i) => (
          <View key={i} style={styles.critRow} wrap={false}>
            <Text style={[styles.critDot, { color: dotColor }]}>•</Text>
            <View style={styles.critBody}>
              <Text style={styles.critLabel}>{c.label}</Text>
              <Text style={styles.critMeta}>
                {c.category}
                {c.origin ? ` · ${c.origin}` : ''}
              </Text>
            </View>
          </View>
        ))
      )}
    </View>
  );
}

export function MembershipPacketPdfDocument({
  data,
}: {
  data: MembershipPacketPdfData;
}): JSX.Element {
  const { brand, labels } = data;
  return (
    <Document>
      <PdfPage size="A4" style={styles.page}>
        <Text style={styles.watermark} fixed>
          {labels.watermarkDraft}
        </Text>

        <View style={styles.header}>
          {brand.logoUrl ? <Image style={styles.logo} src={brand.logoUrl} /> : null}
          <Text style={styles.tenantName}>
            {brand.tenantName}
            {brand.tmaiSiteAbbreviation ? ` (${brand.tmaiSiteAbbreviation})` : ''}
          </Text>
          <View style={[styles.brandBar, { backgroundColor: brand.primaryColor }]} />
          <Text style={styles.docTitle}>{labels.title}</Text>
          <View style={styles.metaRow}>
            <View>
              <Text style={styles.metaLabel}>{labels.stage}</Text>
              <Text style={styles.metaValue}>{data.stage}</Text>
            </View>
            <View>
              <Text style={styles.metaLabel}>{labels.generatedOn}</Text>
              <Text style={styles.metaValue}>{data.generatedOn}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.boardBox}>{labels.boardDecides}</Text>

        <View style={styles.overallRow}>
          <Text style={styles.overallTitle}>{labels.overallStatusTitle}</Text>
          <Text
            style={[
              styles.statusChip,
              data.overallStatus === 'ready' ? styles.chipReady : styles.chipGaps,
            ]}
          >
            {data.overallStatus === 'ready' ? labels.overallReady : labels.overallWithGaps}
          </Text>
        </View>

        <Text style={styles.sectionLabel}>{labels.progressByCategory}</Text>
        {data.byCategory.map((c, i) => (
          <View key={i} style={styles.catRow}>
            <Text style={styles.catName}>{c.label}</Text>
            <Text style={styles.catCount}>
              {c.met}/{c.total}
            </Text>
          </View>
        ))}

        <View style={{ marginTop: 10 }}>
          <Bucket
            title={labels.metTitle}
            dotColor="#16a34a"
            items={data.met}
            emptyLabel={labels.emptyBucket}
          />
          <Bucket
            title={labels.gapsTitle}
            dotColor="#dc2626"
            items={data.gaps}
            emptyLabel={labels.emptyBucket}
          />
          <Bucket
            title={labels.notEvaluableTitle}
            reason={labels.notEvaluableReason}
            dotColor="#94a3b8"
            items={data.notEvaluable}
            emptyLabel={labels.emptyBucket}
          />
          {data.informative.length > 0 ? (
            <Bucket
              title={labels.informativeTitle}
              dotColor="#6366f1"
              items={data.informative}
              emptyLabel={labels.emptyBucket}
            />
          ) : null}
        </View>

        <Text style={styles.footer} fixed>
          {labels.footerDisclaimer}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </PdfPage>
    </Document>
  );
}
